// A markdown link whose target is a WORKSPACE (VFS) PATH — `[the report](spaces/acme/artifacts/report.html)`.
//
// The assistant addresses its files by their agent-facing path and sometimes links one exactly that way. Such
// a target has no scheme, so `safeUrl` lets it through and `resolveInAppPath` does not claim it — and the
// anchor that came out, `<a href="spaces/…" target="_blank">`, resolved against the app's own URL and 404'd
// (the reported "Internal VFS paths used as chat links produce 404"). On the phones the same link was
// rejected as scheme-less and rendered as dead text. This recognises the path so all three surfaces can open
// it the way a `MessageAttachment.vfsPath` chip opens its file.
//
// Pure, no imports: shared by the markdown link classifier and its tests.

/**
 * The top-level roots of the agent's file system, and the CLIENT-side directory each one is addressed by in a
 * `vfsPath` (whose first segment is the `FileSystem` directory's name — see `vfs-path.ts`).
 *
 * The agent-facing mounts are the harness's own (`orion-harness-poc/src/tools/vfs-storage-map.ts`
 * `PLAIN_MOUNTS`, plus `spaces/` from `space-vfs-paths.ts`). Two are renamed at that boundary and are
 * translated back here: `spaces/` is the `workspaces` directory (the same swap `toStoredVfsPath` makes for an
 * attachment), and `best-practices/` is the `genome` directory. `workspaces/` itself is accepted too, because
 * it is the form every persisted attachment path carries. Synthesized trees (`tasks/`, `world.md`) are not
 * files anybody can open and are deliberately absent.
 *
 * Kept 1:1 with iOS `ChatVfsLink.roots` and Android `VFS_LINK_ROOTS` — change one, change all three.
 */
export const VFS_LINK_ROOTS: Readonly<Record<string, string>> = {
  spaces: 'workspaces',
  workspaces: 'workspaces',
  knowledge: 'knowledge',
  brain: 'brain',
  packs: 'packs',
  transcripts: 'transcripts',
  deployedFunctions: 'deployedFunctions',
  'best-practices': 'genome',
};

/** A `scheme:` prefix, per RFC 3986. */
const SCHEME = /^[A-Za-z][A-Za-z0-9+.-]*:/;

/**
 * The `vfsPath` a markdown link target addresses, or null when the target is not a workspace path.
 *
 * Accepts a RELATIVE target only (no scheme, not protocol-relative), tolerates a leading `./` or `/`, and
 * drops a `?query` / `#fragment` — a file is opened, not a position in it. Percent-escapes are decoded, since
 * markdown needs `%20` for a space in a file name. Rejects an empty, `.` or `..` segment rather than
 * normalising it: a path the agent could never have written is not one to guess at. A Space path needs a
 * slug AND a name under it; any other root needs a key under it. A `best-practices/` file whose name starts
 * with `_` is a hidden genome manifest, not a link target.
 */
export function vfsPathOfLink(href: string | undefined | null): string | null {
  let path = href?.trim() ?? '';
  if (!path || SCHEME.test(path) || path.startsWith('//')) return null;
  path = path.replace(/[?#].*$/, '');
  while (path.startsWith('./')) path = path.slice(2);
  path = path.replace(/^\/+/, '');
  try {
    path = decodeURIComponent(path);
  } catch {
    return null;
  }
  const segments = path.split('/');
  // Own keys only: `constructor/x` must not match an inherited property.
  if (!Object.prototype.hasOwnProperty.call(VFS_LINK_ROOTS, segments[0])) return null;
  const directory = VFS_LINK_ROOTS[segments[0]];
  const rest = segments.slice(1);
  if (rest.length === 0 || rest.some((segment) => segment === '' || segment === '.' || segment === '..')) return null;
  if (directory === 'workspaces' && rest.length < 2) return null;
  // The harness's visibility rule (`vfs-storage-map.ts` `toAgentPath`): an `_`-prefixed genome file
  // (`_genome-index.md`, `_brief.md`, …) is the genome service's own manifest, never an agent-facing path.
  if (directory === 'genome' && rest[rest.length - 1].startsWith('_')) return null;
  return [directory, ...rest].join('/');
}

/** The file name a workspace path shows as — its last segment. */
export function vfsFileName(vfsPath: string): string {
  return vfsPath.slice(vfsPath.lastIndexOf('/') + 1);
}
